import { AlertCircle } from 'lucide-react';
import { ContactCTA } from './ContactCTA';

export function ServiceDetailPage({ service }) {
  const { Icon } = service;

  return (
    <div
      className={`py-16 md:py-20 pt-24 md:pt-28 bg-gradient-to-b ${service.bgFrom} via-white ${service.bgTo}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">

          {/* Header */}
          <div className="text-center mb-12 animate-fade-in-up">
            <div
              className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${service.iconGradient} rounded-full shadow-lg mb-6`}
              aria-hidden="true"
            >
              <Icon className="w-10 h-10 text-white" />
            </div>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-800 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="service-title"
            >
              {service.title}
            </h1>
            <p className="text-xl text-gray-600">{service.subtitle}</p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg mb-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-8">{service.description}</p>

            {/* Info Cards */}
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10 list-none p-0 m-0">
              {service.infoCards.map((card, i) => {
                const CardIcon = card.Icon;
                return (
                  <li
                    key={i}
                    className={`bg-gradient-to-br ${card.gradient} rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow duration-200`}
                  >
                    <CardIcon className={`w-8 h-8 ${card.iconColor} mb-3`} aria-hidden="true" />
                    <h2 className="font-semibold text-gray-800 mb-2">{card.title}</h2>
                    <p className="text-gray-700">{card.value}</p>
                  </li>
                );
              })}
            </ul>

            {/* Warning Section */}
            <section className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border-l-4 border-yellow-500 mb-10" aria-labelledby="warning-heading">
              <h2 id="warning-heading" className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" aria-hidden="true" />
                {service.warningTitle}
              </h2>

              {service.warningGroups ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {service.warningGroups.map((group, gi) => (
                    <div key={gi}>
                      <h3 className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">
                        {group.groupTitle}
                      </h3>
                      <ul className="space-y-1.5 text-gray-700 list-none p-0 m-0">
                        {group.items.map((item, ii) => (
                          <li key={ii} className="flex items-start gap-2">
                            <span className="text-orange-500 mt-0.5 flex-shrink-0" aria-hidden="true">&#8226;</span>
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="space-y-2 text-gray-700 list-none p-0 m-0">
                  {service.warningItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-orange-500 mt-0.5 flex-shrink-0" aria-hidden="true">&#8226;</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Details */}
            <h2
              className="text-2xl font-light text-gray-800 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {service.detailsTitle}
            </h2>
            <ul className="space-y-3 mb-10 list-none p-0 m-0">
              {service.details.map((detail, i) => (
                <li
                  key={i}
                  className={`bg-gradient-to-r ${detail.gradient} rounded-xl p-4 border-l-4 ${detail.border} hover:shadow-sm transition-shadow duration-200`}
                >
                  <p className="text-gray-700">
                    <span className="font-semibold">{detail.title}</span>
                    {' – '}
                    {detail.desc}
                  </p>
                </li>
              ))}
            </ul>

            {/* Important Note */}
            <section className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-6 border-l-4 border-cyan-500" aria-labelledby="important-note-heading">
              <h2 id="important-note-heading" className="font-semibold text-gray-800 mb-2">Fontos tudni</h2>
              <p className="text-gray-700 leading-relaxed">{service.importantNote}</p>
              {service.importantNote2 && (
                <p className="text-gray-700 leading-relaxed mt-3">{service.importantNote2}</p>
              )}
              {service.importantNote3 && (
                <p className="text-gray-700 leading-relaxed mt-3">{service.importantNote3}</p>
              )}
            </section>
          </div>

          {/* Results */}
          <section
            className={`bg-gradient-to-br ${service.resultsBgGradient} rounded-3xl p-8 shadow-lg`}
            aria-labelledby="results-heading"
          >
            <h2
              id="results-heading"
              className="text-2xl font-light text-gray-800 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {service.resultsTitle}
            </h2>
            <ul className="space-y-3 text-gray-700 list-none p-0 m-0">
              {service.results.map((result, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className={`w-2 h-2 ${service.resultsBulletColor} rounded-full mt-2 flex-shrink-0`}
                    aria-hidden="true"
                  />
                  <span>{result}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <ContactCTA />
      </div>
    </div>
  );
}
